/**
 * Ships browser logs to CloudWatch instead of the browser console.
 *
 * The production console is silent (see the inline guard in public/index.html),
 * which would otherwise mean losing every diagnostic the app emits. This picks
 * the captured entries up and POSTs them to /graphql/client-logs, where the
 * backend writes them to stdout and the ECS awslogs driver forwards them to the
 * CloudWatch group /ecs/lux-prod-v2-backend.
 *
 * The endpoint sits under /graphql/ deliberately: CloudFront only routes
 * /graphql and /graphql/* to the API origin, so a top-level path would be served
 * by the static bucket and never arrive.
 *
 * Not capturable from JavaScript, no matter what we do here: WebGL driver
 * warnings, CSS keyframe parse warnings and devtools source-map failures are
 * emitted by the browser itself and never pass through console.*.
 */

const ENDPOINT = '/graphql/client-logs';
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH = 50;
// Bound what one page can send. A render loop logging every frame would
// otherwise turn into an unbounded CloudWatch bill.
const MAX_ENTRIES_PER_SESSION = 500;

type Entry = {
  level: string;
  message: string;
  at: string;
  url: string;
  stack?: string;
};

type LuxLogWindow = Window & {
  __luxLogs?: Entry[];
  __luxLogSession?: string;
  __luxLogDropped?: number;
};

let sent = 0;
let timer: number | undefined;

function buffer(): Entry[] {
  const w = window as LuxLogWindow;
  if (!w.__luxLogs) w.__luxLogs = [];
  return w.__luxLogs;
}

function session(): string {
  const w = window as LuxLogWindow;
  if (!w.__luxLogSession) {
    w.__luxLogSession = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }
  return w.__luxLogSession;
}

function post(entries: Entry[], useBeacon: boolean): void {
  if (!entries.length) return;
  const payload = JSON.stringify({ session: session(), entries });

  try {
    // pagehide gets sendBeacon: a normal fetch is cancelled when the document
    // goes away, which is exactly when the most interesting errors happen.
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }));
      return;
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      credentials: 'omit',
      keepalive: true,
    }).catch(() => {
      /* logging must never surface an error of its own */
    });
  } catch {
    /* ignore */
  }
}

function flush(useBeacon = false): void {
  const pending = buffer().splice(0, MAX_BATCH);
  if (!pending.length) return;

  const remaining = Math.max(0, MAX_ENTRIES_PER_SESSION - sent);
  if (remaining === 0) return;

  const batch = pending.slice(0, remaining);
  sent += batch.length;

  if (sent >= MAX_ENTRIES_PER_SESSION) {
    batch.push({
      level: 'warn',
      message: `client log cap reached (${MAX_ENTRIES_PER_SESSION} entries); further entries dropped for this session`,
      at: new Date().toISOString(),
      url: window.location.href,
    });
  }
  post(batch, useBeacon);
}

export function startRemoteLogging(): void {
  const w = window as LuxLogWindow;

  // Errors that never reach console.*: an uncaught throw and a rejected promise
  // with no handler. These are the two most valuable things to capture.
  window.addEventListener('error', (event) => {
    buffer().push({
      level: 'error',
      message: event.message || 'uncaught error',
      stack: event.error?.stack,
      at: new Date().toISOString(),
      url: window.location.href,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { message?: string; stack?: string } | string | undefined;
    buffer().push({
      level: 'error',
      message:
        typeof reason === 'string' ? reason : reason?.message || 'unhandled promise rejection',
      stack: typeof reason === 'object' ? reason?.stack : undefined,
      at: new Date().toISOString(),
      url: window.location.href,
    });
  });

  // Flush on the way out, while the entries can still be sent.
  window.addEventListener('pagehide', () => flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });

  timer = window.setInterval(() => flush(false), FLUSH_INTERVAL_MS);

  // Anything the early guard captured before this module loaded (including the
  // vendored potree/three scripts, which log while the app bundle is still
  // downloading) is already sitting in the buffer — send it now.
  if (w.__luxLogDropped) {
    buffer().push({
      level: 'warn',
      message: `${w.__luxLogDropped} early entries dropped before the logger started`,
      at: new Date().toISOString(),
      url: window.location.href,
    });
  }
  flush(false);
}

export function stopRemoteLogging(): void {
  if (timer !== undefined) window.clearInterval(timer);
  timer = undefined;
}
